// src/components/contracts/CompactDocumentUpload.jsx
import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Image, Archive, File, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Componentă compactă pentru încărcarea documentelor semnate
 * Versiune minimalistă pentru a fi folosită în carduri
 * 
 * @param {string} token - Token-ul de acces
 * @param {Function} onUploadSuccess - Callback la succes
 * @param {string} uploadType - Tipul documentului: 'contract' | 'additional-act' | 'notice'
 * @param {number} entityId - ID-ul entității (actId sau noticeId)
 * @param {string} buttonText - Text pentru butonul de upload (opțional)
 * @param {string} buttonVariant - Varianta butonului: 'primary' | 'success' | 'danger' (opțional)
 */
const CompactDocumentUpload = ({ 
  token, 
  onUploadSuccess, 
  uploadType = 'contract',
  entityId = null,
  buttonText = 'Încarcă Documente',
  buttonVariant = 'primary'
}) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showFileList, setShowFileList] = useState(false);
  const fileInputRef = useRef(null);

  const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.zip', '.7z', '.rar'];
  const maxFiles = 25;
  const maxFileSizeMB = 10;

  const getFileIcon = (fileName) => {
    const ext = fileName.toLowerCase().split('.').pop();
    const iconClass = "w-4 h-4";

    if (['jpg', 'jpeg', 'png'].includes(ext)) {
      return <Image className={`${iconClass} text-blue-500`} />;
    }
    if (['zip', '7z', 'rar'].includes(ext)) {
      return <Archive className={`${iconClass} text-orange-500`} />;
    }
    if (['pdf'].includes(ext)) {
      return <FileText className={`${iconClass} text-red-500`} />;
    }
    if (['doc', 'docx'].includes(ext)) {
      return <FileText className={`${iconClass} text-blue-600`} />;
    }
    return <File className={`${iconClass} text-gray-500`} />;
  };

  const validateFile = (file) => {
    const ext = '.' + file.name.toLowerCase().split('.').pop();

    if (!allowedExtensions.includes(ext)) {
      return `Tipul fișierului "${ext}" nu este permis`;
    }

    if (file.size > maxFileSizeMB * 1024 * 1024) {
      return `Fișierul "${file.name}" depășește ${maxFileSizeMB}MB`;
    }

    return null;
  };

  const handleFiles = (fileList) => {
    if (!fileList) return;

    const newFiles = [];
    const errors = [];

    Array.from(fileList).forEach((file) => {
      const validationError = validateFile(file);

      if (validationError) {
        errors.push(validationError);
        return;
      }

      if (files.length + newFiles.length >= maxFiles) {
        errors.push(`Maximum ${maxFiles} fișiere permise`);
        return;
      }

      const id = `${Date.now()}-${Math.random()}`;
      newFiles.push({ file, id });
    });

    if (errors.length > 0) {
      toast.error(errors.join('; '));
      return;
    }

    setFiles(prev => [...prev, ...newFiles]);
    setShowFileList(true);
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
    if (files.length <= 1) {
      setShowFileList(false);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Selectați cel puțin un fișier');
      return;
    }

    if ((uploadType === 'additional-act' || uploadType === 'notice') && !entityId) {
      toast.error('ID-ul entității lipsește');
      return;
    }

    setUploading(true);

    try {
      const { contractAccessService } = await import('../../services/contractAccessService');
      const filesToUpload = files.map(f => f.file);

      let data;
      
      switch (uploadType) {
        case 'additional-act':
          data = await contractAccessService.uploadAdditionalActDocuments(token, entityId, filesToUpload);
          break;
        case 'notice':
          data = await contractAccessService.uploadNoticeDocuments(token, entityId, filesToUpload);
          break;
        case 'contract':
        default:
          data = await contractAccessService.uploadSignedDocuments(token, filesToUpload);
          break;
      }

      toast.success(`${files.length} document(e) încărcat(e) cu succes!`);
      
      // Reset state
      setFiles([]);
      setShowFileList(false);

      if (onUploadSuccess) {
        onUploadSuccess(data);
      }
    } catch (err) {
      console.error('Upload error:', err);
      
      let errorMessage = 'Eroare la încărcarea documentelor';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.Message) {
        errorMessage = err.response.data.Message;
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getButtonClasses = () => {
    const baseClasses = "w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm";
    
    switch (buttonVariant) {
      case 'success':
        return `${baseClasses} bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400`;
      case 'danger':
        return `${baseClasses} bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400`;
      case 'primary':
      default:
        return `${baseClasses} bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400`;
    }
  };

  return (
    <div className="space-y-3">
      {/* File Input Hidden */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={allowedExtensions.join(',')}
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
      />

      {/* Select Files Button */}
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className={getButtonClasses()}
      >
        <Upload className="w-4 h-4" />
        {buttonText}
      </button>

      {/* File List (Compact) */}
      {showFileList && files.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-700">
              {files.length} fișier(e) selectat(e)
            </span>
            <button
              onClick={() => {
                setFiles([]);
                setShowFileList(false);
              }}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Șterge tot
            </button>
          </div>

          <div className="max-h-32 overflow-y-auto space-y-1">
            {files.map(({ file, id }) => (
              <div
                key={id}
                className="flex items-center justify-between bg-gray-50 p-2 rounded text-xs"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {getFileIcon(file.name)}
                  <span className="truncate text-gray-700">{file.name}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-gray-500">{formatFileSize(file.size)}</span>
                  <button
                    onClick={() => removeFile(id)}
                    disabled={uploading}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="w-full bg-primary-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-primary-700 disabled:bg-gray-400 transition-colors flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Se încarcă...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Încarcă {files.length} document(e)
              </>
            )}
          </button>
        </div>
      )}

      {/* Help Text */}
      <p className="text-xs text-gray-500 text-center">
        PDF, DOC, imagini sau arhive (max {maxFileSizeMB}MB)
      </p>
    </div>
  );
};

export default CompactDocumentUpload;